import { Typography } from "@mui/material";
import { useState } from "react";

const MedicationAccordion = ({
  isMobile,
  medicationName = "Unknown",
  therapyType = "Unknown",
  dosage = "Unknown",
  prescribed,
  status = "Unconfirmed",
  dosageInstructions,
  prescriber,
  quantity = "",
  duration = "unknown",
  refills = "N/A",
  route = "Unknown",
  frequency = "Unknown",
  asNeeded = null,
  isDarkTheme = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };

  // Generate styles based on current state
  const styles = createStyles(isMobile, isOpen, isDarkTheme);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <span style={styles.pillIcon}>⊘</span>
          <div style={styles.medicationInfo}>
            <h6 style={styles.medicationName}>{medicationName}</h6>
            <span style={styles.medicationSubtitle}>{therapyType}</span>
          </div>
        </div>
        <div style={styles.headerRight}>
          <span style={styles.dosage}>{dosage}</span>
          <span style={styles.confirmed}>{status}</span>
          <span style={styles.chevron} onClick={toggleAccordion}>
            {"▾"}
          </span>
        </div>
      </div>

      <div style={styles.content}>
        <div style={styles.dosageInstructions}>
          <h5 style={styles.infoTitle}>Dosage Instructions</h5>
          <h6 style={styles.prescribedDate}>Prescribed: {prescribed}</h6>
        </div>

        <Typography style={styles.instructionText}>
          {dosageInstructions}
        </Typography>

        <Typography style={styles.instructionText}>{prescriber}</Typography>

        <div style={styles.infoRow}>
          <div style={styles.infoBox}>
            <h5 style={styles.infoTitle}>Supply</h5>
            <Typography style={styles.instructionText}>{quantity}</Typography>
            <div style={styles.instructionText}>
              Duration: <span>{duration}</span>
            </div>
            <div style={styles.instructionText}>
              Refills: <span>{refills}</span>
            </div>
          </div>

          <div style={styles.infoBoxDark}>
            <div style={styles.infoDetail}>
              <span style={styles.infoText}>Route:</span>
              <span style={styles.instructionText}>{route}</span>
            </div>
            <div style={styles.infoDetail}>
              <span style={styles.infoText}>Frequency:</span>
              <span style={styles.instructionText}>{frequency}</span>
            </div>
            <div style={styles.infoDetail}>
              <span style={styles.infoText}>As Needed:</span>
              <span style={styles.instructionText}>
                {asNeeded === null ? "Unknown" : asNeeded ? "Yes" : "No"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Extracted styles object
const createStyles = (isMobile, isOpen, isDarkTheme) => ({
  // Container styles
  container: {
    fontFamily: "Arial, sans-serif",
    backgroundColor: isDarkTheme ? "#444387" : "#e6e7ff",
    color: isDarkTheme ? "white" : "#333",
    borderRadius: "12px",
    padding: isMobile ? "0.5rem" : "0.75rem",
    maxWidth: "100%",
    margin: "0px",
    fontSize: isMobile ? "0.875rem" : "1rem",
  },

  // Header styles
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: isOpen
      ? isDarkTheme
        ? "1px solid rgba(255, 255, 255, 0.2)"
        : "1px solid rgba(0, 0, 0, 0.1)"
      : "none",
    flexDirection: isMobile ? "column" : "row",
    gap: isMobile ? "0.5rem" : "0",
  },

  headerLeft: {
    display: "flex",
    alignItems: "center",
    width: isMobile ? "100%" : "auto",
  },

  pillIcon: {
    transform: "rotate(135deg)",
    marginRight: "0.75rem",
    fontSize: isMobile ? "1.125rem" : "1.5rem",
  },

  medicationInfo: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },

  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: isMobile ? "0.5rem" : "0.75rem",
    width: isMobile ? "100%" : "auto",
    justifyContent: isMobile ? "flex-end" : "flex-start",
  },

  // Typography styles
  medicationName: {
    fontSize: isMobile ? "0.875rem" : "1rem",
    margin: "0 0 0.25rem 0",
    color: isDarkTheme ? "#FFFFFF" : "#333",
  },

  medicationSubtitle: {
    fontSize: isMobile ? "0.75rem" : "0.875rem",
    color: isDarkTheme ? "rgba(255, 255, 255, 0.8)" : "rgba(0, 0, 0, 0.7)",
  },

  therapyType: {
    margin: "0px",
    fontSize: isMobile ? "0.75rem" : "0.875rem",
    color: isDarkTheme ? "rgba(255, 255, 255, 0.8)" : "rgba(0, 0, 0, 0.7)",
  },

  // Badge/label styles
  dosage: {
    backgroundColor: isDarkTheme ? "#8B9AFF" : "#7986f5",
    color: isDarkTheme ? "white" : "white",
    padding: isMobile ? "0.25rem 0.5rem" : "0.5rem 0.75rem",
    borderRadius: "20px",
    textAlign: "center",
    whiteSpace: "nowrap",
    fontSize: isMobile ? "0.75rem" : "0.875rem",
  },

  confirmed: {
    backgroundColor: isDarkTheme ? "#4CAF50" : "#4CAF50",
    color: "white",
    padding: isMobile ? "0.25rem 0.5rem" : "0.5rem 0.75rem",
    borderRadius: "20px",
    fontWeight: "bold",
    fontSize: isMobile ? "0.75rem" : "0.875rem",
  },

  // Toggle styles
  chevron: {
    fontSize: isMobile ? "1.125rem" : "1.5rem",
    cursor: "pointer",
    transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
    transition: "transform 0.3s ease",
    color: isDarkTheme ? "white" : "#333",
  },

  // Content styles
  content: {
    display: isOpen ? "block" : "none",
    padding: "0.5rem 0",
  },

  dosageInstructions: {
    color: isDarkTheme ? "rgba(138, 138, 255, 1)" : "#5D5FEF",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: isMobile ? "column" : "row",
    gap: isMobile ? "0.25rem" : "0",
    textAlign: isMobile ? "left" : "inherit",
  },

  prescribedDate: {
    color: isDarkTheme ? "white" : "#333",
    fontSize: isMobile ? "0.75rem" : "0.875rem",
  },

  instructionText: {
    fontSize: isMobile ? "0.75rem" : "0.875rem",
    margin: "0.5rem 0",
    color: isDarkTheme ? "#FFFFFF" : "#333",
  },

  // Info section styles
  infoRow: {
    display: "flex",
    gap: isMobile ? "0.5rem" : "1rem",
    marginTop: "1rem",
    flexDirection: isMobile ? "column" : "row",
  },

  infoBox: {
    flex: 1,
    backgroundColor: isDarkTheme ? "#8A8AFF" : "#d3d4ff",
    padding: isMobile ? "0.5rem" : "0.75rem",
    borderRadius: "10px",
  },

  infoBoxDark: {
    flex: 1,
    backgroundColor: isDarkTheme ? "#52519F" : "#b8baf0",
    padding: isMobile ? "0.5rem" : "0.75rem",
    borderRadius: "10px",
  },

  infoTitle: {
    fontSize: isMobile ? "0.875rem" : "1.125rem",
    margin: "0 0 0.5rem 0",
    color: isDarkTheme ? "#FFFFFF" : "#333",
  },

  infoDetail: {
    display: "flex",
    justifyContent: "start",
    alignItems: "center",
    marginBottom: "0.25rem",
  },

  infoText: {
    fontSize: isMobile ? "0.75rem" : "0.875rem",
    color: isDarkTheme ? "#FFFFFF" : "#333",
    marginRight: "0.5rem",
  },

  infoTextBold: {
    fontSize: isMobile ? "0.875rem" : "1rem",
    fontWeight: "bold",
    color: isDarkTheme ? "#FFFFFF" : "#333",
  },
});

export default MedicationAccordion;
